// --- Session persistence: save/restore app state to localStorage ---

import type { EnrichedData, POI, PoiCategory, RouteProcessingSettings, TraceData } from "../types";
import { CORRIDOR_M } from "./poi-config";

// --- Schema versioning ---

/** Bump when the persisted shape changes in a breaking way: older sessions are then dropped */
const SCHEMA_VERSION = 4;

const STORAGE_KEY = "ravitools_session";

// --- Persisted shape (subset of app state, JSON-serializable) ---

interface PersistedSession {
  version: number;
  savedAt: string; // ISO timestamp
  /** Active category names */
  activeCategories: PoiCategory[];
  /** Loaded traces (multi-GPX) */
  traces: TraceData[];
  /** Found POIs */
  pois: POI[];
  /** Enrichment results: stored as [id, data][] for JSON compat */
  enrichments: [string, EnrichedData][];
  /** User preferences */
  enrichAll: boolean;
  routeSettings: RouteProcessingSettings;
}

// --- Public API ---

export interface SessionSnapshot {
  activeCategories: Set<PoiCategory>;
  traces: TraceData[];
  pois: POI[];
  enrichments: Map<string, EnrichedData>;
  enrichAll: boolean;
  routeSettings: RouteProcessingSettings;
  savedAt: string;
}

/** Serialize a snapshot to the versioned wire format (localStorage + .ravitools.json). */
export function serializeSession(snapshot: Omit<SessionSnapshot, "savedAt">): string {
  const data: PersistedSession = {
    version: SCHEMA_VERSION,
    savedAt: new Date().toISOString(),
    activeCategories: [...snapshot.activeCategories],
    traces: snapshot.traces,
    pois: snapshot.pois,
    enrichments: [...snapshot.enrichments.entries()],
    enrichAll: snapshot.enrichAll,
    routeSettings: snapshot.routeSettings,
  };
  return JSON.stringify(data);
}

/**
 * Parse + validate the wire format. Returns null on version mismatch or
 * corrupt payload (per-element validation).
 */
export function parseSession(json: string): SessionSnapshot | null {
  try {
    const data: PersistedSession = JSON.parse(json);

    // Version gate
    if (data.version !== SCHEMA_VERSION) return null;

    // Basic shape validation
    if (
      !Array.isArray(data.pois) ||
      !Array.isArray(data.enrichments) ||
      !Array.isArray(data.traces)
    ) {
      return null;
    }

    // Per-element validation: a partially-corrupt or older payload can pass the array
    // checks above yet still crash downstream when fields are missing.
    const tracesOk = data.traces.every(
      (t) =>
        !!t &&
        Array.isArray((t as { original?: unknown }).original) &&
        Array.isArray((t as { simplified?: unknown }).simplified),
    );
    const poisOk = data.pois.every(
      (p) =>
        !!p &&
        typeof (p as { lat?: unknown }).lat === "number" &&
        typeof (p as { lon?: unknown }).lon === "number" &&
        (p as { category?: unknown }).category != null,
    );
    if (!tracesOk || !poisOk) return null;

    return {
      activeCategories: new Set(data.activeCategories),
      traces: data.traces,
      pois: data.pois,
      enrichments: new Map(data.enrichments),
      enrichAll: data.enrichAll ?? false,
      // The slider once went to 3000 m: never past its end
      routeSettings: {
        maxDistanceM: Math.min(data.routeSettings?.maxDistanceM ?? CORRIDOR_M, CORRIDOR_M),
      },
      savedAt: data.savedAt,
    };
  } catch {
    return null;
  }
}

/**
 * Save current session state to localStorage.
 * Returns false (and warns) if the write fails, e.g. quota exceeded, so callers
 * can tell the user that "resume session" won't work, instead of failing silently.
 */
export function saveSession(snapshot: Omit<SessionSnapshot, "savedAt">): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, serializeSession(snapshot));
    return true;
  } catch (err) {
    console.warn(
      "Ravitools: session save failed (localStorage full or unavailable); resume won't work this session.",
      err,
    );
    return false;
  }
}

/**
 * Load a previously saved session from localStorage.
 * Returns null if no session exists, version mismatch, or data is corrupt.
 */
export function loadSession(): SessionSnapshot | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = parseSession(raw);
    if (!parsed) clearSession();
    return parsed;
  } catch {
    clearSession();
    return null;
  }
}

/**
 * Check if a saved session exists (without parsing it).
 */
export function hasSession(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Clear saved session from localStorage.
 */
export function clearSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // silently skip
  }
}
